import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {IInfoUser} from '../typeMembers'
import {useFormik} from 'formik'
import {useEffect} from 'react'
import {useMutation, useQuery} from '@apollo/client'
import {ADD_MEMBERS} from '../../../gql/mutations/membersMutation'
import {toast} from 'react-toastify'
import {IResCreateMember, IResEditMember} from '../../../types/users'
import {parseId} from '../../../helpers'
import {GET_ROLES_GROUP} from '../../../gql/queries/rolesGroupQuery'
import {IResRolesGroups} from '../../../types/roles'
import {EDIT_USER} from '../../../gql/mutations/userMutations'
import {MEMBER_MESSAGES as M, memberValidationSchema} from '../memberMessages'

interface IModalAddMemberProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoEdit?: null | IInfoUser
}

export const ModalAddMember = ({isOpen, onClose, infoEdit, refetch}: IModalAddMemberProps) => {
  const intl = useIntl()
  const {
    data: dataRoles,
    loading: loadingRoles,
    error: errorRoles,
  } = useQuery<IResRolesGroups>(GET_ROLES_GROUP, {fetchPolicy: 'no-cache'})

  const [gqlCreate, {data, loading, error}] = useMutation<IResCreateMember>(ADD_MEMBERS)
  const [gqlEdit, {data: dataEdit, loading: loadingEdit, error: errorEdit}] =
    useMutation<IResEditMember>(EDIT_USER)
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      rol: '',
      email: '',
      cellphone: '',
    },
    validationSchema: memberValidationSchema(intl),
    onSubmit: (v) => {
      const userData = {
        ...v,
        rol: [parseId(v.rol)],
      }
      if (infoEdit) {
        gqlEdit({
          variables: {
            userData,
            userId: parseId(infoEdit.id),
          },
        })
      } else {
        gqlCreate({
          variables: {
            userData,
          },
        })
      }
    },
  })

  useEffect(() => {
    if (infoEdit) {
      formik.setValues({
        email: infoEdit.email,
        lastName: infoEdit.lastName,
        firstName: infoEdit.name,
        cellphone: infoEdit.phone,
        rol: infoEdit.rol,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoEdit])
  useEffect(() => {
    if (errorEdit)
      toast.error(intl.formatMessage(M.errorWithMessage, {message: errorEdit.message}))
  }, [errorEdit, intl])

  useEffect(() => {
    if (error) toast.error(intl.formatMessage(M.errorWithMessage, {message: error.message}))
  }, [error, intl])
  useEffect(() => {
    if (errorRoles)
      toast.error(intl.formatMessage(M.errorWithMessage, {message: errorRoles.message}))
  }, [errorRoles, intl])

  useEffect(() => {
    if (!data) return
    if (data?.createUserMutation?.user?.id) {
      toast.success(intl.formatMessage(M.toastCreated))
      refetch()
      onClose()
    } else if (data?.createUserMutation.errors?.length > 0) {
      toast.error(
        intl.formatMessage(M.errorWithMessage, {
          message:
            data?.createUserMutation.errors[0]?.message || intl.formatMessage(M.somethingWrong),
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])
  useEffect(() => {
    if (!dataEdit) return
    if (dataEdit?.updateUserMutation?.user?.id) {
      toast.success(intl.formatMessage(M.toastUpdated))
      refetch()
      onClose()
    } else if (dataEdit?.updateUserMutation.errors?.length > 0) {
      toast.error(
        intl.formatMessage(M.errorWithMessage, {
          message:
            dataEdit?.updateUserMutation.errors[0]?.message || intl.formatMessage(M.somethingWrong),
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataEdit])

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage(infoEdit ? M.modalEdit : M.modalAdd)}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <div className='d-flex gap-3 flex-column'>
            <Form.Group controlId='name'>
              <Form.Label>{intl.formatMessage(M.firstName)}</Form.Label>
              <Form.Control
                type='text'
                placeholder={intl.formatMessage(M.placeholderFirstName)}
                name='name'
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={Boolean(formik.touched.firstName && formik.errors.firstName)}
              />
              <Form.Control.Feedback type='invalid'>
                {formik.errors.firstName}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId='lastName'>
              <Form.Label>{intl.formatMessage(M.lastName)}</Form.Label>
              <Form.Control
                type='text'
                placeholder={intl.formatMessage(M.placeholderLastName)}
                name='lastName'
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={Boolean(formik.touched.lastName && formik.errors.lastName)}
              />
              <Form.Control.Feedback type='invalid'>{formik.errors.lastName}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId='rol'>
              <Form.Label>
                {intl.formatMessage(M.role)}
                <small className='text-danger ms-1'>{intl.formatMessage(M.required)}</small>
              </Form.Label>
              <Form.Select
                name='rol'
                value={formik.values.rol}
                onChange={formik.handleChange}
                isInvalid={Boolean(formik.errors.rol && formik.touched.rol)}
                disabled={loadingRoles}
              >
                {loadingRoles ? (
                  <option value=''>{intl.formatMessage(M.loading)}</option>
                ) : (
                  <option value=''>{intl.formatMessage(M.selectOne)}</option>
                )}
                {dataRoles?.allGroups.edges.map(({node}) => (
                  <option key={node.id} value={node.id}>
                    {node.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type='invalid'>{formik.errors.rol}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId='email'>
              <Form.Label>{intl.formatMessage(M.email)}</Form.Label>
              <Form.Control
                type='email'
                placeholder={intl.formatMessage(M.placeholderEmail)}
                name='email'
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={Boolean(formik.touched.email && formik.errors.email)}
              />
              <Form.Control.Feedback type='invalid'>{formik.errors.email}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId='phone'>
              <Form.Label>{intl.formatMessage(M.phone)}</Form.Label>
              <Form.Control
                type='text'
                placeholder={intl.formatMessage(M.placeholderPhone)}
                name='cellphone'
                value={formik.values.cellphone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={Boolean(formik.touched.cellphone && formik.errors.cellphone)}
              />
              <Form.Control.Feedback type='invalid'>
                {formik.errors.cellphone}
              </Form.Control.Feedback>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            {intl.formatMessage(M.close)}
          </Button>
          <Button
            type='submit'
            variant='primary'
            disabled={loading || loadingEdit}
            className='d-flex align-items-center gap-2'
          >
            {(loading || loadingEdit) && <Spinner animation='border' size='sm' color='light' />}
            {intl.formatMessage(M.saveChanges)}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
